/* eslint-disable max-statements */
import { useForm, zodResolver } from '@mantine/form';
import { cloneDeep, omit } from 'lodash';
import { toast } from 'react-toastify';

import { Declaration, EnvironmentCountries, TransportEquipment } from '@e-origin/shared';

import { DECLARATION_CUSTOMS_PROCEDURE_LENGTH, DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR } from '@constants';
import { useUpdateDeclarationDataLrn, useUpdatePartialDeclaration } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { DeclarationGeneralFormValues, declarationGeneralSchema } from './validateDeclarationGeneralForm';

export const useDeclarationGeneralForm = ({ editDisabled, declaration }) => {
  const { data: settings } = useSettings();

  const saveDeclarationDataLRNMutation = useUpdateDeclarationDataLrn();
  const savePartialDataToDeclarationMutation = useUpdatePartialDeclaration();

  const isHighValue = isHighValueDeclaration(declaration.generalInfo.group);
  const declarationTypeDisabled = (!isHighValue && settings?.country === EnvironmentCountries.NL) || editDisabled;
  const isExport = declaration?.generalInfo?.isExport;

  const buildContainers = (transportEquipment: TransportEquipment) => {
    if (!isHighValue) {
      return;
    }

    const containers = transportEquipment?.containers?.length
      ? transportEquipment.containers
      : [
          {
            identificationNumber: '',
            seals: [],
          },
        ];

    if (transportEquipment?.seals?.length) {
      containers[0].seals.push(...transportEquipment.seals);
    }

    return containers;
  };

  const buildFormValues = (d: Declaration) => ({
    messageInfo: {
      declarationMainType: d?.messageInfo?.declarationMainType || '',
      declarationType: d?.messageInfo?.declarationType,
      acceptanceDate: d?.messageInfo?.acceptanceDate,
      security: d?.messageInfo?.security,
    },
    generalInfo: {
      group: d?.generalInfo?.group,
    },
    consignment: {
      carrier: d?.consignment.carrier,
      carrierHeader: d?.consignment.carrierHeader,
      referenceNumberUCR: d?.consignment?.referenceNumberUCR,
      referenceNumberUCRHeader: d?.consignment?.referenceNumberUCRHeader,
      customsOfficePresentation: {
        issuingPlaceCode: d?.consignment?.customsOfficePresentation?.issuingPlaceCode,
        referenceNumber: d?.consignment?.customsOfficePresentation?.referenceNumber,
      },
      customsSupervisingOffice: {
        referenceNumber: d?.consignment?.customsSupervisingOffice?.referenceNumber,
      },
      customsOfficeExport: {
        referenceNumber: d?.consignment?.customsOfficeExport?.referenceNumber,
      },
      locationOfGoods: {
        unLocode: d?.consignment?.locationOfGoods?.unLocode,
      },
      countryOfDispatch: d?.consignment?.countryOfDispatch,
      countryOfDispatchHeader: d?.consignment?.countryOfDispatchHeader,
      destinationHeader: d?.consignment?.destinationHeader,
      countryOfDestination: d?.consignment?.countryOfDestination,
      regionOfDestination: d?.consignment?.regionOfDestination,
      countryOfExportHeader: d?.consignment?.countryOfExportHeader,
      countryOfExport: d?.consignment?.countryOfExport,
      customsOfficeExit: {
        referenceNumber: d?.consignment?.customsOfficeExit?.referenceNumber,
      },
    },
    otherDataElements: {
      registrationNumber: d?.otherDataElements?.registrationNumber,
      natureOfTransaction: d?.otherDataElements?.natureOfTransaction,
      natureOfTransactionHeader: d?.otherDataElements?.natureOfTransactionHeader,
    },
    customsProcedureCurrent: d?.goodShipmentGlobal?.customsProcedure?.substring(0, DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR),
    customsProcedurePrevious: d?.goodShipmentGlobal?.customsProcedure?.substring(
      DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR,
      DECLARATION_CUSTOMS_PROCEDURE_LENGTH,
    ),
    goodShipmentGlobal: {
      procedureHeader: d?.goodShipmentGlobal?.procedureHeader ?? true,
      customsProcedureType: d?.goodShipmentGlobal?.customsProcedureType,
      customsNationalProcedure: d?.goodShipmentGlobal?.customsNationalProcedure,

      customsNationalProcedureCode: d?.goodShipmentGlobal?.customsNationalProcedureCode,
      preference: d?.goodShipmentGlobal?.preference,
    },
    valuationInformationTaxes: {
      deliveryTerms: {
        incotermCode: d?.valuationInformationTaxes?.deliveryTerms?.incotermCode,
        location: d?.valuationInformationTaxes?.deliveryTerms?.location,
        country: d?.valuationInformationTaxes?.deliveryTerms?.country,
      },
    },
    transportInformation: {
      inlandModeOfTransport: d?.transportInformation?.inlandModeOfTransport,
      modeOfTransportMeansAtTheBorder: d?.transportInformation?.modeOfTransportMeansAtTheBorder,
      arrivalTransportMeans: {
        typeOfIdentification: d?.transportInformation?.arrivalTransportMeans?.typeOfIdentification,
        identificationNumber: d?.transportInformation?.arrivalTransportMeans?.identificationNumber,
      },
      departureTransportMeans: {
        typeOfIdentification: d?.transportInformation?.departureTransportMeans?.typeOfIdentification,
        identificationNumber: d.transportInformation?.departureTransportMeans?.identificationNumber,
        nationality: d.transportInformation?.departureTransportMeans?.nationality,
      },
      activeBorderTransportMeans: {
        nationality: d.transportInformation?.activeBorderTransportMeans?.nationality,
        typeOfIdentification: d.transportInformation?.activeBorderTransportMeans?.typeOfIdentification,
        identificationNumber: d.transportInformation?.activeBorderTransportMeans?.identificationNumber,
      },
      transportEquipment: {
        header: d.transportInformation?.transportEquipment?.header,
        containerIdentification: d.transportInformation?.transportEquipment?.containerIdentification,
        containers: buildContainers(d.transportInformation?.transportEquipment),
      },
      countryOfRoutingOfConsignment: d.transportInformation?.countryOfRoutingOfConsignment?.length
        ? d.transportInformation?.countryOfRoutingOfConsignment
        : [{ country: '' }],
    },
    declarationGoods: {
      additionalProcedure: declaration.goodsShipmentItems[0].additionalProcedure || [],
      packaging: {
        typeOfPackages: (d.goodsShipmentItems || [])[0]?.packaging?.typeOfPackages,
      },
    },
    customsState: {
      bcpMrn: declaration.customsState?.bcpMrn,
    },
  });

  const form = useForm<DeclarationGeneralFormValues>({
    initialValues: buildFormValues(declaration),
    validate: zodResolver(declarationGeneralSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values) => {
    const partialDeclaration = cloneDeep(omit(values, 'declarationGoods', 'customsProcedureCurrent', 'customsProcedurePrevious'));

    partialDeclaration.goodShipmentGlobal.customsProcedure = (values.customsProcedureCurrent || '') + (values.customsProcedurePrevious || '');

    if (partialDeclaration.transportInformation?.transportEquipment?.containers?.length) {
      partialDeclaration.transportInformation.transportEquipment.containers = partialDeclaration.transportInformation.transportEquipment.containers
        .filter((container) => !!container.identificationNumber)
        .map((container) => ({
          ...container,
          seals: container.seals?.filter((s) => !!s.identificationNumber) || [],
        }));
    }

    if (partialDeclaration.transportInformation?.countryOfRoutingOfConsignment?.length) {
      partialDeclaration.transportInformation.countryOfRoutingOfConsignment = partialDeclaration.transportInformation.countryOfRoutingOfConsignment.filter(
        (c) => !!c.country,
      );
    }

    if (values.declarationGoods?.additionalProcedure) {
      values.declarationGoods.additionalProcedure = values.declarationGoods.additionalProcedure.filter((procedure) => procedure !== '');
    }

    if (isHighValueDeclaration(declaration.generalInfo.group)) {
      await saveDeclarationDataLRNMutation.mutateAsync({
        lrn: declaration.messageInfo.LRN,
        sequence: declaration.goodShipmentGlobal.sequence,
        partialDeclaration,
        partialGoodsShipmentItem: values.declarationGoods,
      });

      if (form.getDirty()['goodShipmentGlobal.preference']) {
        toast.warn('Preference has changed. Check origin!');
      }
    } else {
      savePartialDataToDeclarationMutation.mutateAsync({
        id: declaration._id,
        partialDeclaration: omit(values, 'declarationGoods'),
        partialGoodsShipmentItem: values.declarationGoods,
      });
    }
  };

  return {
    form,
    isExport,
    declarationTypeDisabled,
    isSaving: saveDeclarationDataLRNMutation.isPending || savePartialDataToDeclarationMutation.isPending,
    onSubmit,
    buildFormValues,
  };
};
